import { useState } from 'react';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { ShieldCheck, KeyRound, Check } from 'lucide-react';
import { useAccessToken } from '../auth/useAuth';
import { AddPasskeyForm, AddTotpForm } from './FactorForms';
import {
  ENROLLMENT_WALL_BODY, NO_PASSKEY_SUPPORT, PASSKEY_HINT,
  BACKUP_PROMPT_LABEL, BACKUP_PROMPT_BODY, BACKUP_DECLINE,
} from './copy';

const ICON_STROKE = 1.75;

type Step = 'primary' | 'backup';

/**
 * The enrollment hard wall's actual UI (Stream 4 Phase 1). Reached only when
 * {@link EnrollmentGate} has confirmed the signed-in user has zero usable
 * factors — no dashboard, vault, or workspace picker is reachable behind it,
 * and there is deliberately no "skip for now": once a first factor is active
 * the user can continue, but they always land here again on a future session
 * if that factor is ever lost (appcore blocks removing a last factor, so
 * "zero factors" should only ever happen on a brand-new account).
 *
 * A passkey is the primary method (one tap, no code to type or lose); TOTP is
 * offered as the fallback when this browser/device can't do WebAuthn at all,
 * and — per docs/28 §2/§9 — as the RECOMMENDED backup once a passkey is in
 * place, since each factor is the other's recovery redundancy. Skipping the
 * backup is allowed (a single usable factor already satisfies the floor) but
 * is never framed as "skip" — it's a plain "Continue without a backup" (shared
 * with the onboarding path via ./copy), with the gap left visible afterward in
 * the Sign-in keys section of Settings → Sign-in and security.
 */
export function EnrollmentWall({ onEnrolled }: { onEnrolled: () => void }) {
  const token = useAccessToken();
  const supportsPasskey = browserSupportsWebAuthn();
  const [step, setStep] = useState<Step>('primary');
  const [primaryDone, setPrimaryDone] = useState(false);

  if (!token) return null;

  return (
    <div className="mx-auto flex max-w-[440px] flex-col items-center pt-[6vh] text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-token bg-bronze/12 text-bronze">
        <ShieldCheck size={26} strokeWidth={ICON_STROKE} aria-hidden />
      </div>
      <h1 className="mb-2 mt-5 font-serif text-[26px] font-normal leading-tight tracking-[-0.01em] text-navy">
        Let’s protect your account
      </h1>
      <p className="m-0 mb-8 max-w-[380px] font-sans text-[14px] leading-relaxed text-mid">
        {ENROLLMENT_WALL_BODY}
      </p>

      <div className="w-full text-left">
        {step === 'primary' && (
          <PrimaryStep
            token={token}
            supportsPasskey={supportsPasskey}
            onDone={() => {
              setPrimaryDone(true);
              if (supportsPasskey) setStep('backup'); else onEnrolled();
            }}
          />
        )}
        {step === 'backup' && primaryDone && (
          <BackupStep token={token} onContinue={onEnrolled} />
        )}
      </div>
    </div>
  );
}

function PrimaryStep({ token, supportsPasskey, onDone }: { token: string; supportsPasskey: boolean; onDone: () => void }) {
  return supportsPasskey
    ? <AddPasskeyForm token={token} buttonLabel="Add your passkey" hint={PASSKEY_HINT} onDone={onDone} />
    : (
      <div>
        <p className="mb-4 text-center text-[12px] text-slate font-sans">
          {NO_PASSKEY_SUPPORT}
        </p>
        <AddTotpForm token={token} onDone={onDone} />
      </div>
    );
}

function BackupStep({ token, onContinue }: { token: string; onContinue: () => void }) {
  const [adding, setAdding] = useState(false);
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-token bg-bronze/12 text-bronze">
          <Check size={18} strokeWidth={ICON_STROKE} aria-hidden />
        </div>
        <div className="mb-4 text-[13px] text-navy font-sans">Authenticator app added. You’re all set.</div>
        <button onClick={onContinue} className="w-full px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans">
          Continue to Steward
        </button>
      </div>
    );
  }

  if (adding) {
    return <AddTotpForm token={token} onDone={() => setDone(true)} />;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3 rounded-token border border-line bg-cream/40 px-4 py-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-token bg-bronze/12 text-bronze">
          <Check size={16} strokeWidth={ICON_STROKE} aria-hidden />
        </span>
        <span className="text-[13px] text-navy font-sans">Your passkey is set up.</span>
      </div>

      <div className="mb-2 text-[13px] font-semibold text-navy font-sans">{BACKUP_PROMPT_LABEL}</div>
      <p className="mb-4 text-[12px] leading-relaxed text-slate font-sans">
        {BACKUP_PROMPT_BODY}
      </p>
      <button
        onClick={() => setAdding(true)}
        className="flex w-full items-center justify-center gap-2 px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans"
      >
        <KeyRound size={15} strokeWidth={ICON_STROKE} aria-hidden />
        Add an authenticator app
      </button>
      <button
        onClick={onContinue}
        className="mt-2.5 w-full px-4 py-2 text-[11px] font-semibold cursor-pointer bg-transparent border-none text-slate font-sans hover:text-navy"
      >
        {BACKUP_DECLINE}
      </button>
    </div>
  );
}
